/**
 * Per-route SEO definitions for the marketing and legal pages.
 *
 * Kept in one file so the uniqueness constraints that matter to search engines
 * — every indexable route needs its own title, description and canonical — are
 * verifiable by reading a single list rather than by auditing a dozen
 * components.
 *
 * Titles are written to sit under ~60 characters so they aren't truncated in
 * results, and descriptions between ~120 and ~158 characters for the same
 * reason. Both lead with what the page actually does; neither repeats the other.
 */

import { SITE_ORIGIN, SITE_NAME, type SeoInput } from "./seo";

const LOGO = `${SITE_ORIGIN}/brand/bunnycal-mark-512.png`;

/**
 * The publisher entity. Referenced by `@id` from the other blocks so Google
 * resolves one organisation across the site instead of inferring a separate one
 * per page — the "clear entity relationships" AI-search engines rely on too.
 */
export const ORGANIZATION_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_ORIGIN}/#organization`,
  name: SITE_NAME,
  url: `${SITE_ORIGIN}/`,
  logo: {
    "@type": "ImageObject",
    url: LOGO,
    width: 512,
    height: 512,
  },
  description:
    "BunnyCal is scheduling software that turns one shareable link into confirmed meetings, with calendar conflict detection and automatic time zone handling.",
} as const;

export const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_ORIGIN}/#website`,
  name: SITE_NAME,
  url: `${SITE_ORIGIN}/`,
  publisher: { "@id": `${SITE_ORIGIN}/#organization` },
  inLanguage: "en",
} as const;

/**
 * SoftwareApplication rather than a bare Product: BunnyCal is a web app, and the
 * more specific type lets Google surface the price range and category. The two
 * Offers mirror what LandingPricing renders — Free at $0 and Professional at
 * $6/month ($5 billed yearly), Team $29/month ($25 billed yearly). Keep these in sync with
 * prices that contradict the visible page are a structured-data violation.
 */
export const PRODUCT_LD = {
  "@context": "https://schema.org",
  "@type": ["Product", "SoftwareApplication"],
  name: SITE_NAME,
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "Scheduling",
  operatingSystem: "Web",
  url: `${SITE_ORIGIN}/pricing`,
  publisher: { "@id": `${SITE_ORIGIN}/#organization` },
  offers: [
    {
      "@type": "Offer",
      name: "Free",
      price: "0",
      priceCurrency: "USD",
      description: "One event type, one connected calendar, and unlimited bookings.",
      url: `${SITE_ORIGIN}/pricing`,
      availability: "https://schema.org/InStock",
    },
    {
      "@type": "Offer",
      name: "Professional",
      price: "5",
      priceCurrency: "USD",
      description:
        "Unlimited event types, multiple calendars, group events, booking forms, embeds and your own branding. $5 a month when billed yearly.",
      url: `${SITE_ORIGIN}/pricing`,
      availability: "https://schema.org/InStock",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "5",
        priceCurrency: "USD",
        // Not per seat: one flat price covering one person.
        unitText: "month",
      },
    },
    {
      "@type": "Offer",
      name: "Team",
      price: "25",
      priceCurrency: "USD",
      description:
        "Everything in Professional plus round-robin and collective events, team workspaces and white-label, for up to 5 seats (including you). $25 a month when billed yearly.",
      url: `${SITE_ORIGIN}/pricing`,
      availability: "https://schema.org/InStock",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "25",
        priceCurrency: "USD",
        unitText: "month",
      },
    },
  ],
} as const;

/** Breadcrumbs for a one-level-deep marketing page. */
function breadcrumb(name: string, path: string) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
      { "@type": "ListItem", position: 2, name, item: `${SITE_ORIGIN}${path}` },
    ],
  };
}

/**
 * The homepage title carries the brand, the on-page H1 ("Your calendar,
 * simplified.") and the category keyword. Matching the H1 matters: Google
 * rewrites a title it judges inconsistent with the page, and the rewrite it had
 * been showing ("Scheduling that feels calm, not heavy") came from an old build
 * and no longer existed in the source at all.
 */
export const HOME_SEO: SeoInput = {
  title: "BunnyCal - Your calendar, simplified. Free scheduling software",
  description:
    "Share one booking link and let people pick a time that works. BunnyCal reads your calendars for conflicts, handles time zones, and confirms in seconds. Free to start.",
  path: "/",
  jsonLd: [ORGANIZATION_LD, WEBSITE_LD],
};

/**
 * Pricing FAQ content — the single source for BOTH the rendered accordion
 * (LandingFAQ.tsx) and the FAQPage JSON-LD below.
 *
 * They must not be maintained separately: Google requires FAQ structured data to
 * match text visible on the page, so a schema-only answer, or one that drifts
 * from the rendered copy, is a rich-result violation. Every answer here is also
 * consistent with the plan cards in LandingPricing.tsx — if a price changes,
 * change it in all three places (or the markup contradicts the page).
 */
export const PRICING_FAQS = [
  // Ordered deliberately. The general questions come first, in the order somebody weighing the
  // plans would ask them; the two "how do I actually buy this" answers close the list, where a
  // reader has the context to act on them. `lead: true` marks those for the small label the
  // accordion renders; nothing else about them is special.
  {
    question: "How much does BunnyCal cost?",
    answer:
      "Professional is $5 a month billed yearly, or $6 month to month, and covers one person. Team is $25 a month billed yearly, or $29 month to month, and covers up to 5 seats, the team owner included. Prices are per plan, not per seat.",
  },
  {
    question: "What is the difference between Professional and Team?",
    answer:
      "Professional is for one person: unlimited event types and calendars, group events, booking forms, embeds, and your own logo, colours and brand name. Team adds the things that need more than one host - round-robin and collective events, and team workspaces - plus white-label included.",
  },
  {
    question: "Is BunnyCal free to use?",
    answer:
      "Yes. The Free plan covers one event type, one connected calendar and unlimited bookings, and needs no credit card to start.",
  },
  {
    question: "Can I change plan or cancel at any time?",
    answer:
      "Yes. Upgrading, downgrading and adding or removing the white-label option all take effect immediately, and plans can be cancelled at any time. No credit card is required to start on the Free plan.",
  },
  {
    question: "Which calendars does BunnyCal connect to?",
    answer:
      "Google Calendar and Microsoft Outlook, including multiple accounts at once. BunnyCal reads every connected calendar for conflicts while writing new bookings to just one.",
  },
  {
    question: "How do I remove BunnyCal branding from my booking page?",
    answer:
      "Team includes it. On Professional it is a $3 a month option you can switch on from Billing in your dashboard - your plan changes to Professional with white-label and the mark comes off every page you share, straight away. Switch it off again the same way.",
    lead: true,
  },
  {
    question: "What if my team is larger than 5 seats?",
    answer:
      "Team also comes in a 10-seat size at $41 a month billed yearly, or $49 month to month. Choose it from Billing in your dashboard, or switch to it later - moving between the two sizes is a plan change, not a new subscription. For more than 10 seats, get in touch.",
    lead: true,
  },
] as const;

const PRICING_FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: PRICING_FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

export const PRICING_SEO: SeoInput = {
  title: "Pricing - BunnyCal scheduling plans from $0",
  description:
    "Start free with one event type and unlimited bookings. Professional is $5 a month billed yearly ($6 monthly), and Team adds round-robin, collective events and team workspaces from $25 a month.",
  path: "/pricing",
  jsonLd: [PRODUCT_LD, PRICING_FAQ_LD, breadcrumb("Pricing", "/pricing")],
};

/**
 * Questions people actually type alongside "calendly alternative".
 *
 * Single source for the rendered accordion on the page and the FAQPage JSON-LD
 * below, for the same reason as PRICING_FAQS: schema that does not match the
 * visible answer is a rich-result violation. Every answer here is about
 * BunnyCal — nothing asserts what a competitor does or does not do.
 */
export const CALENDLY_ALTERNATIVE_FAQS = [
  {
    question: "Is BunnyCal free?",
    answer:
      "Yes. The free plan covers one meeting type, one connected calendar and unlimited bookings, with no credit card to start. Paid plans begin at $5 a month billed yearly.",
  },
  {
    question: "Does BunnyCal work with Google Calendar?",
    answer:
      "Yes. Connect a Google account and BunnyCal reads it for conflicts before offering any time, and writes confirmed bookings back to it.",
  },
  {
    question: "Does BunnyCal work with Microsoft Outlook?",
    answer:
      "Yes. Microsoft calendars are supported the same way as Google, including Outlook and Microsoft 365 accounts.",
  },
  {
    question: "Can I connect more than one calendar?",
    answer:
      "The free plan connects one calendar. Paid plans connect as many as you like, and every one of them is checked for conflicts before a slot is offered - so a personal commitment blocks a work booking.",
  },
  {
    question: "Can teams use BunnyCal?",
    answer:
      "Yes, on a paid plan. Teams can distribute incoming meetings automatically with round-robin, book several hosts into one meeting, or run a single session for many guests.",
  },
  {
    question: "Do I need a credit card to try it?",
    answer:
      "No. The free plan needs no card and does not expire. You only enter payment details if you choose to upgrade.",
  },
] as const;

const CALENDLY_ALTERNATIVE_FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: CALENDLY_ALTERNATIVE_FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

/**
 * "calendly alternative" is the highest commercial-intent query in this market:
 * someone typing it has already decided they want a scheduling tool and is
 * actively shopping. The page exists to answer that query directly rather than
 * make them infer the answer from /pricing.
 *
 * Deliberately NOT carrying PRODUCT_LD — that block hardcodes
 * `url: ${SITE_ORIGIN}/pricing`, so emitting it here would attach pricing-page
 * offers to a different URL. /pricing already carries it.
 *
 * It DOES carry FAQ structured data, because the page now renders those exact
 * answers in a visible accordion. The rule in this file is that FAQ JSON-LD
 * must mirror copy the visitor can read — doubly so on a page that names a
 * competitor, where a claim only a crawler can see is a claim nobody can check.
 * CALENDLY_ALTERNATIVE_FAQS is the single source for both.
 */
export const CALENDLY_ALTERNATIVE_SEO: SeoInput = {
  // Matches the on-page h1. Google rewrites a title it judges inconsistent
  // with the page, and this page's h1 is the clearest statement of what it is.
  title: "A simpler, more affordable Calendly alternative",
  description:
    "Looking for a Calendly alternative? BunnyCal is free to start, checks every connected calendar so you are never double-booked, and is $5 a month when you grow. No credit card.",
  path: "/calendly-alternative",
  jsonLd: [
    ORGANIZATION_LD,
    CALENDLY_ALTERNATIVE_FAQ_LD,
    breadcrumb("Calendly alternative", "/calendly-alternative"),
  ],
};

/**
 * Paid-traffic landing page for solo professionals who searched for a Calendly alternative.
 *
 * `noindex` on purpose: /calendly-alternative already owns that query organically, and a second
 * indexable page on the same intent would make the two compete. This one exists to convert ad
 * clicks, so it is shorter, price-led and has one call to action. Google Ads reads noindex pages
 * fine; only a robots.txt Disallow would block AdsBot.
 */
export const SWITCH_FROM_CALENDLY_FAQS = [
  {
    question: "Is BunnyCal really free?",
    answer:
      "Yes. The Free plan covers one meeting type, one connected calendar and unlimited bookings, with no credit card and no time limit. You only pay if you choose to upgrade.",
  },
  {
    question: "I need round-robin for a team. Is that included?",
    answer:
      "Round-robin and collective events are on the Team plan, which starts at $25 a month billed yearly for up to 5 people. Professional is for one person.",
  },
  {
    question: "What do I get on Professional for $5 a month?",
    answer:
      "Unlimited meeting types, as many connected calendars as you like, group events, booking forms, embeds and your own logo, colours and brand name. It is $5 a month billed yearly, or $6 month to month, and covers one person.",
  },
  {
    question: "Will it stop me being double-booked?",
    answer:
      "BunnyCal reads every calendar you connect, Google and Microsoft Outlook included, and only offers times when you are free on all of them. Everyone sees the times in their own time zone.",
  },
  {
    question: "Does it add a video link to the invite?",
    answer:
      "Yes. Zoom, Google Meet or Microsoft Teams links are added to each confirmed booking automatically.",
  },
  {
    question: "Can I cancel or change plan later?",
    answer:
      "Yes. You can upgrade, downgrade or cancel at any time, and changes take effect immediately.",
  },
] as const;

export const SWITCH_FROM_CALENDLY_SEO: SeoInput = {
  title: "Switch from Calendly - BunnyCal from $0, Professional $5/mo",
  description:
    "A simpler Calendly alternative for consultants, coaches and freelancers. Free to start with unlimited bookings, then $5 a month billed yearly. No credit card.",
  path: "/switch-from-calendly",
  noindex: true,
};

/**
 * Round-robin landing page for small teams. Indexable: there is no other page on the site for
 * "round robin scheduling", and it carries unique content (how assignment works, team pricing).
 * The FAQ answers are the single source for the rendered accordion and the FAQPage JSON-LD.
 */
export const ROUND_ROBIN_FAQS = [
  {
    question: "How does round-robin scheduling work in BunnyCal?",
    answer:
      "A guest sees the combined availability of your whole team and never picks a person. When they confirm a time, BunnyCal assigns the booking to whichever host is free at that time and was assigned a booking least recently.",
  },
  {
    question: "What if a teammate is on holiday or busy?",
    answer:
      "A host who is not free for the whole meeting is skipped for that time, so a booking only ever goes to someone who can actually take it. They are back in the rotation as soon as they are free again.",
  },
  {
    question: "How much does the Team plan cost?",
    answer:
      "Team for up to 5 people is $25 a month billed yearly, or $29 month to month. Team for up to 10 people is $41 a month billed yearly, or $49 month to month. The price is per plan, not per seat.",
  },
  {
    question: "Is round-robin included on the Professional plan?",
    answer:
      "No. Round-robin and collective events are part of the Team plan. Professional, at $5 a month billed yearly, is for one person.",
  },
  {
    question: "What if my team is bigger than 10 people?",
    answer: "Get in touch through the contact page and we will work out a plan with you.",
  },
  {
    question: "Which calendars does it work with?",
    answer:
      "Google Calendar and Microsoft Outlook, including several accounts at once. Each host's calendars are checked for conflicts before a time is offered, and Zoom, Google Meet or Microsoft Teams links are added to every booking.",
  },
  {
    question: "Do I need a credit card to start?",
    answer:
      "No. You can create an account and connect your calendar on the Free plan with no card. Round-robin needs the Team plan, so you only add payment details when you upgrade.",
  },
] as const;

const ROUND_ROBIN_FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: ROUND_ROBIN_FAQS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: { "@type": "Answer", text: item.answer },
  })),
};

export const ROUND_ROBIN_SEO: SeoInput = {
  title: "Round-robin scheduling for teams - $25/mo for 5 people",
  description:
    "Share one booking link and BunnyCal books the next available teammate. Team plans cover up to 5 people from $25 a month or up to 10 from $41, billed yearly. Flat price, not per seat.",
  path: "/round-robin-scheduling",
  jsonLd: [
    ORGANIZATION_LD,
    ROUND_ROBIN_FAQ_LD,
    breadcrumb("Round-robin scheduling", "/round-robin-scheduling"),
  ],
};

export const ABOUT_SEO: SeoInput = {
  title: "About BunnyCal - Why we built a calmer scheduler",
  description:
    "BunnyCal is built by a small team who thought booking a meeting should take one link and a few seconds. Here's the thinking behind the product.",
  path: "/about",
  jsonLd: [ORGANIZATION_LD, breadcrumb("About", "/about")],
};

export const PRIVACY_SEO: SeoInput = {
  title: "Privacy Policy - BunnyCal",
  description:
    "How BunnyCal collects, uses and protects your data, including calendar access, third-party integrations, retention periods and your rights over your information.",
  path: "/privacy",
  jsonLd: [breadcrumb("Privacy Policy", "/privacy")],
};

export const TERMS_SEO: SeoInput = {
  title: "Terms of Service - BunnyCal",
  description:
    "The terms governing your use of BunnyCal, covering accounts, acceptable use, subscriptions and billing, cancellation, liability and changes to the service.",
  path: "/terms",
  jsonLd: [breadcrumb("Terms of Service", "/terms")],
};

export const CONTACT_SEO: SeoInput = {
  title: "Contact BunnyCal - Support and sales",
  description:
    "Get in touch with the BunnyCal team about support, billing questions, integrations or anything else. We read everything and reply quickly.",
  path: "/contact",
  jsonLd: [
    {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact BunnyCal",
      url: `${SITE_ORIGIN}/contact`,
      publisher: { "@id": `${SITE_ORIGIN}/#organization` },
    },
    breadcrumb("Contact", "/contact"),
  ],
};

export const ZOOM_DOCS_SEO: SeoInput = {
  title: "Zoom integration for BunnyCal - Setup guide",
  description:
    "Connect Zoom to BunnyCal so every confirmed booking gets a Zoom meeting link automatically. Installation, permissions, troubleshooting and how to remove the app.",
  path: "/docs/zoom",
  jsonLd: [
    {
      "@context": "https://schema.org",
      "@type": "TechArticle",
      headline: "Zoom integration for BunnyCal",
      description:
        "How to connect, use and remove the BunnyCal Zoom integration so confirmed bookings get a Zoom link automatically.",
      url: `${SITE_ORIGIN}/docs/zoom`,
      publisher: { "@id": `${SITE_ORIGIN}/#organization` },
      author: { "@type": "Organization", name: "BunnyCal" },
      dateModified: "2026-07-28",
      inLanguage: "en",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Docs", item: `${SITE_ORIGIN}/docs/` },
        { "@type": "ListItem", position: 3, name: "Zoom", item: `${SITE_ORIGIN}/docs/zoom` },
      ],
    },
  ],
};

/**
 * Sign-in is deliberately `noindex`: it holds no content worth ranking, and
 * robots.txt already disallows it. The tag is belt-and-braces for the case where
 * the page is reached via a link Google has already discovered.
 */
export const SIGN_IN_SEO: SeoInput = {
  // noindex, so this is the browser-tab label rather than a search result. It covers sign-up too:
  // the same page creates an account for anyone who does not have one yet.
  title: "Sign in or sign up - BunnyCal",
  description: "Sign in to BunnyCal or create an account to manage your booking links, availability and meetings.",
  path: "/sign-in",
  noindex: true,
};

/** Public SPA routes that receive route-specific static head shells at build time. */
export const PUBLIC_ROUTE_SEO = {
  pricing: PRICING_SEO,
  "calendly-alternative": CALENDLY_ALTERNATIVE_SEO,
  "switch-from-calendly": SWITCH_FROM_CALENDLY_SEO,
  "round-robin-scheduling": ROUND_ROBIN_SEO,
  about: ABOUT_SEO,
  privacy: PRIVACY_SEO,
  terms: TERMS_SEO,
  contact: CONTACT_SEO,
  "docs-zoom": ZOOM_DOCS_SEO,
  "sign-in": SIGN_IN_SEO,
} as const;
